import React, { useEffect, useState, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import axiosInstance from "../services/axiosInstance";

/**
 * Renders a collapsible left panel showing the uploaded prescription file.
 */
const UploadFilePanel = ({ children, uploadData: uploadDataProp = null }) => {
    const location = useLocation();
    const uploadId = location.state?.fromUploadId;
    const isActive = !!uploadId;

    const [upload,     setUpload]    = useState(uploadDataProp || null);
    const [loading,    setLoading]   = useState(false);
    const [collapsed,  setCollapsed] = useState(false);
    const [panelWidth, setPanelWidth] = useState(30);
    const dragging = useRef(false);
    const startX   = useRef(0);
    const startW   = useRef(30);

    useEffect(() => {
        if (!uploadId) return;
        setLoading(true);
        axiosInstance.get(`/api/prescription-uploads/${uploadId}/`)
            .then(res => setUpload(res.data))
            .catch(console.error)
            .finally(() => setLoading(false));
    }, [uploadId]);

    useEffect(() => {
        if (uploadDataProp) setUpload(uploadDataProp);
        else if (!uploadId) setUpload(null);
    }, [uploadDataProp]);

    // CSS injection — must run before the dialog opens so it constrains the dialog
    // to the right panel. This only applies in splitMode (navigated from upload list).
    useEffect(() => {
        if (!isActive) return;
        document.body.classList.add('upload-panel-active');

        const styleId = 'upload-panel-style';
        let style = document.getElementById(styleId);
        if (!style) {
            style = document.createElement('style');
            style.id = styleId;
            document.head.appendChild(style);
        }
        const rightWidth = collapsed ? 100 : (100 - panelWidth);
        style.textContent = `
            body.upload-panel-active .p-dialog-mask {
                left: auto !important;
                right: 0 !important;
                width: ${rightWidth}% !important;
                top: 0 !important;
                bottom: 0 !important;
                position: fixed !important;
            }
            body.upload-panel-active .p-dialog.p-dialog-maximized {
                width: 100% !important;
                height: 100% !important;
                max-height: 100% !important;
                margin: 0 !important;
                border-radius: 0 !important;
                top: 0 !important;
                left: 0 !important;
            }
        `;

        return () => {
            document.body.classList.remove('upload-panel-active');
            document.getElementById(styleId)?.remove();
        };
    }, [isActive, panelWidth, collapsed]);

    // useCallback (empty deps) so these are the SAME function reference on
    // every render — onMouseMove/onMouseUp only ever touch refs and the
    // (stable) setPanelWidth setter, so this is safe. That stability is what
    // lets the unmount-cleanup effect below reliably remove exactly the
    // listener(s) that are actually attached, no matter which render's
    // onMouseDown attached them.
    const onMouseMove = useCallback((e) => {
        if (!dragging.current) return;
        const dx     = e.clientX - startX.current;
        const totalW = window.innerWidth;
        const newW   = Math.min(70, Math.max(25, startW.current + (dx / totalW) * 100));
        setPanelWidth(newW);
    }, []);

    const onMouseUp = useCallback(() => {
        dragging.current = false;
        document.removeEventListener("mousemove", onMouseMove);
        document.removeEventListener("mouseup", onMouseUp);
    }, [onMouseMove]);

    const onMouseDown = (e) => {
        dragging.current = true;
        startX.current   = e.clientX;
        startW.current   = panelWidth;
        document.addEventListener("mousemove", onMouseMove);
        document.addEventListener("mouseup", onMouseUp);
    };

    // Belt-and-suspenders: if the component unmounts mid-drag (e.g. navigating
    // away before releasing the mouse), onMouseUp never fires and the listeners
    // above would otherwise stay bound to document forever.
    useEffect(() => {
        return () => {
            document.removeEventListener("mousemove", onMouseMove);
            document.removeEventListener("mouseup", onMouseUp);
        };
    }, [onMouseMove, onMouseUp]);

    if (!isActive) return <>{children}</>;

    return (
        <div style={{ display: "flex", height: "calc(100vh - 60px)", overflow: "hidden" }}>
            {/* Left: uploaded file */}
            {!collapsed && (
                <div style={{
                    width: `${panelWidth}%`, background: "#1a1a2e",
                    display: "flex", flexDirection: "column", flexShrink: 0, position: "relative",
                }}>
                    <div style={{
                        padding: "10px 14px", background: "#0f0f1a", borderBottom: "1px solid #333",
                        display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
                    }}>
                        <div style={{ minWidth: 0 }}>
                            {loading ? (
                                <span style={{ color: "#aaa", fontSize: 13 }}>Loading…</span>
                            ) : upload ? (
                                <>
                                    <div style={{ color: "white", fontSize: 13, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                        {upload.original_name}
                                    </div>
                                    <div style={{ color: "#888", fontSize: 11.5, marginTop: 1 }}>
                                        {upload.customer_name}
                                        {upload.notes && <span style={{ fontStyle: "italic", marginLeft: 6 }}>· "{upload.notes}"</span>}
                                    </div>
                                </>
                            ) : null}
                        </div>
                        <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                            {upload?.file_url && (
                                <a href={upload.file_url} target="_blank" rel="noreferrer"
                                    style={{ color: "#60a5fa", fontSize: 11, textDecoration: "none", display: "flex", alignItems: "center", gap: 4, padding: "4px 8px", borderRadius: 6, border: "1px solid #334" }}>
                                    <i className="pi pi-external-link" style={{ fontSize: 11 }} /> Full
                                </a>
                            )}
                            <button onClick={() => setCollapsed(true)}
                                style={{ background: "none", border: "1px solid #334", borderRadius: 6, color: "#888", cursor: "pointer", padding: "4px 8px", fontSize: 11, display: "flex", alignItems: "center", gap: 4 }}>
                                <i className="pi pi-chevron-left" style={{ fontSize: 10 }} /> Hide
                            </button>
                        </div>
                    </div>
                    <div style={{ flex: 1, overflow: "hidden" }}>
                        {upload && (
                            upload.file_type === "pdf" ? (
                                <iframe src={upload.file_url} title="Prescription" style={{ width: "100%", height: "100%", border: "none" }} />
                            ) : (
                                <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", overflow: "auto", padding: 12 }}>
                                    <img src={upload.file_url} alt="Uploaded prescription" style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
                                </div>
                            )
                        )}
                    </div>
                </div>
            )}

            {/* Drag handle */}
            {!collapsed && (
                <div onMouseDown={onMouseDown}
                    style={{ width: 5, background: "#2d2d2d", cursor: "col-resize", flexShrink: 0, transition: "background 0.15s", zIndex: 10 }}
                    onMouseEnter={e => e.currentTarget.style.background = "#4a90e2"}
                    onMouseLeave={e => e.currentTarget.style.background = "#2d2d2d"}
                    title="Drag to resize"
                />
            )}

            {/* Right: page content */}
            <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0, overflow: "hidden" }}>
                {collapsed && (
                    <div style={{ flexShrink: 0, padding: "8px 12px", borderBottom: "1px solid var(--border-color, #e2e8f0)" }}>
                        <button onClick={() => setCollapsed(false)}
                            style={{ background: "var(--brand-primary)", border: "none", borderRadius: 8, color: "white", cursor: "pointer", padding: "6px 12px", fontSize: 12, fontWeight: 500, fontFamily: "inherit", display: "flex", alignItems: "center", gap: 6, boxShadow: "0 2px 8px rgba(0,0,0,0.15)" }}>
                            <i className="pi pi-image" style={{ fontSize: 12 }} /> Show Prescription
                        </button>
                    </div>
                )}
                <div id="upload-right-panel" style={{ flex: 1, overflow: "auto", position: "relative", minWidth: 0, height: "100%", background: "var(--surface-ground, var(--surface-bg))" }}>
                    {children}
                </div>
            </div>
        </div>
    );
};

export default UploadFilePanel;